import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, MapPin, BadgeCheck, Star, Shield, MessageCircle, ChevronLeft, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setLiked(r.data.liked);
      setCount(r.data.likes_count);
      api.get("/products", { params: { category: r.data.category } }).then((rr) =>
        setRelated(rr.data.filter((x) => x.id !== id).slice(0, 5))
      );
    });
  }, [id]);

  const like = async () => {
    if (!user) return navigate("/login");
    const { data } = await api.post(`/products/${id}/like`);
    setLiked(data.liked);
    setCount(data.likes_count);
  };

  const startChat = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post("/conversations", { product_id: id });
      navigate(`/chat/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai chat");
    }
  };

  const buy = () => {
    if (!user) return navigate("/login");
    navigate(`/checkout/${id}`);
  };

  if (!p) return <div className="py-20 text-center text-gray-400">Memuat...</div>;

  const isOwner = user?.id === p.seller_id;
  const sold = p.status === "sold";

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 text-sm mb-4">
        <ChevronLeft className="w-4 h-4" /> Kembali
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Images */}
        <div className="lg:col-span-6">
          <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100">
            <img src={fileUrl((p.images || [])[imgIdx])} alt={p.title} className="w-full h-full object-cover" />
          </div>
          {(p.images || []).length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${i === imgIdx ? "border-[#0B4DA2]" : "border-transparent"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#0B4DA2] font-medium">{p.condition}</span>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2">{p.title}</h1>
              <div className="text-2xl sm:text-3xl font-extrabold text-[#0B4DA2] mt-2" data-testid="product-price">{formatIDR(p.price)}</div>
            </div>
            <button data-testid="product-detail-like" onClick={like} className="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center shrink-0">
              <Heart className={`w-5 h-5 ${liked ? "fill-[#E0533C] text-[#E0533C]" : "text-gray-500"}`} />
            </button>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-500 mt-3">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {p.location}</span>
            <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {count} suka</span>
          </div>

          {/* Seller */}
          <div
            onClick={() => navigate(`/user/${p.seller.id}`)}
            className="mt-5 flex items-center gap-3 p-3 rounded-2xl border border-gray-100 bg-white cursor-pointer"
            data-testid="seller-card"
          >
            <img src={p.seller.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
            <div className="flex-1">
              <div className="flex items-center gap-1 font-semibold text-gray-900">
                {p.seller.name}
                {p.seller.verified && <BadgeCheck className="w-4 h-4 text-[#0B4DA2]" />}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {p.seller.rating} • {p.seller.location}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mt-5">
            <h3 className="font-semibold text-gray-900 mb-1">Deskripsi</h3>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{p.description || "Tidak ada deskripsi."}</p>
          </div>

          {/* Safe pay */}
          <div className="mt-5 flex items-start gap-3 p-3 rounded-2xl bg-emerald-50 border border-emerald-100">
            <Shield className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800">
              <b>Kalabiru Safe Pay</b> — dana ditahan sampai barang kamu terima. Aman dari penipuan.
            </div>
          </div>

          {/* CTA */}
          {!isOwner && !sold && (
            <div className="mt-6 grid grid-cols-2 gap-3 sticky bottom-20 md:static">
              <button data-testid="product-chat-button" onClick={startChat} className="flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-[#0B4DA2] text-[#0B4DA2] font-semibold">
                <MessageCircle className="w-5 h-5" /> Tawar / Chat
              </button>
              <button data-testid="product-buy-now-button" onClick={buy} className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#0B4DA2] hover:bg-[#083B7C] text-white font-semibold transition">
                <Truck className="w-5 h-5" /> Beli Langsung
              </button>
            </div>
          )}
          {isOwner && <div className="mt-6 text-sm text-gray-400 text-center py-3 bg-gray-50 rounded-xl">Ini produk milik kamu.</div>}
          {sold && <div className="mt-6 text-sm text-white text-center py-3 bg-gray-800 rounded-xl font-semibold">Produk sudah terjual.</div>}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Barang serupa</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
